/**
 * thermalPrinterService.ts
 * Impresión térmica vía Web Bluetooth (ESC/POS)
 * Soporta impresoras 58mm y 80mm
 *
 * Marcas compatibles: Epson, Star, Xprinter, GOOJPRT, MTP-II, Rongta, etc.
 * (cualquier impresora que hable ESC/POS por Bluetooth)
 */

// ─── UUID de servicio Bluetooth estándar para impresoras ESC/POS ─────────────
const PRINTER_SERVICE_UUID       = '000018f0-0000-1000-8000-00805f9b34fb';
const PRINTER_CHARACTERISTIC_UUID = '00002af1-0000-1000-8000-00805f9b34fb';

// UUID alternativo (algunas marcas chinas usan este)
const PRINTER_SERVICE_UUID_ALT       = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const PRINTER_CHARACTERISTIC_UUID_ALT = '49535343-8841-43f4-a8d4-ecbe34729bb3';

// ─── Comandos ESC/POS ─────────────────────────────────────────────────────────
const ESC = 0x1b;
const GS  = 0x1d;

const CMD = {
  INIT:          [ESC, 0x40],                  // Inicializar impresora
  ALIGN_LEFT:    [ESC, 0x61, 0x00],
  ALIGN_CENTER:  [ESC, 0x61, 0x01],
  ALIGN_RIGHT:   [ESC, 0x61, 0x02],
  BOLD_ON:       [ESC, 0x45, 0x01],
  BOLD_OFF:      [ESC, 0x45, 0x00],
  DOUBLE_SIZE:   [GS,  0x21, 0x11],            // Doble ancho + alto
  NORMAL_SIZE:   [GS,  0x21, 0x00],
  CUT:           [GS,  0x56, 0x41, 0x10],      // Corte parcial
  FEED_3:        [ESC, 0x64, 0x03],            // Avanzar 3 líneas
  LINE_FEED:     [0x0a],
};

// ─── Estado global (persiste en la sesión) ────────────────────────────────────
let _device:         BluetoothDevice | null         = null;
let _characteristic: BluetoothRemoteGATTCharacteristic | null = null;
let _paperWidth:     58 | 80 = 80;

/** Ancho en caracteres según papel */
const CHARS = () => _paperWidth === 58 ? 32 : 48;

// ─── Helpers de texto ─────────────────────────────────────────────────────────

const encoder = new TextEncoder();

function encode(text: string): Uint8Array {
  return encoder.encode(text);
}

function bytes(...cmds: number[]): Uint8Array {
  return new Uint8Array(cmds);
}

/** Centra texto rellenando con espacios */
function center(text: string, width = CHARS()): string {
  if (text.length >= width) return text.substring(0, width);
  const pad = Math.floor((width - text.length) / 2);
  return ' '.repeat(pad) + text;
}

/** Línea separadora */
function divider(char = '-', width = CHARS()): string {
  return char.repeat(width);
}

/** Fila de dos columnas: etiqueta a la izquierda, valor a la derecha */
function row(label: string, value: string, width = CHARS()): string {
  const maxLabel = Math.floor(width * 0.55);
  const maxValue = width - maxLabel - 1;
  const l = label.substring(0, maxLabel).padEnd(maxLabel);
  const v = value.substring(0, maxValue).padStart(maxValue);
  return l + ' ' + v;
}

/** Concatenar múltiples Uint8Array */
function concat(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

// ─── Conexión Bluetooth ───────────────────────────────────────────────────────

export interface PrinterStatus {
  connected: boolean;
  deviceName: string | null;
  paperWidth: 58 | 80;
}

export function getPrinterStatus(): PrinterStatus {
  return {
    connected:  !!(_device && _device.gatt?.connected && _characteristic),
    deviceName: _device?.name ?? null,
    paperWidth: _paperWidth,
  };
}

export function setPaperWidth(width: 58 | 80): void {
  _paperWidth = width;
  localStorage.setItem('printer_paper_width', String(width));
}

/** Carga configuración guardada */
export function loadPrinterConfig(): void {
  const w = localStorage.getItem('printer_paper_width');
  if (w === '58' || w === '80') _paperWidth = w;
}

/** Intenta reconectar al dispositivo emparejado previamente */
export async function reconnect(): Promise<boolean> {
  if (!_device) return false;
  try {
    await _connect(_device);
    return true;
  } catch {
    return false;
  }
}

/** Solicita al usuario seleccionar una impresora Bluetooth */
export async function requestPrinter(paperWidth: 58 | 80 = 80): Promise<void> {
  if (!navigator.bluetooth) {
    throw new Error('Tu navegador no soporta Web Bluetooth. Usa Chrome en Android o Chrome desktop.');
  }

  _paperWidth = paperWidth;
  localStorage.setItem('printer_paper_width', String(paperWidth));

  const device = await navigator.bluetooth.requestDevice({
    // Acepta cualquier dispositivo con el servicio de impresora ESC/POS
    filters: [
      { services: [PRINTER_SERVICE_UUID] },
      { services: [PRINTER_SERVICE_UUID_ALT] },
    ],
    optionalServices: [PRINTER_SERVICE_UUID, PRINTER_SERVICE_UUID_ALT],
  }).catch(async () => {
    // Fallback: acepta cualquier dispositivo Bluetooth
    return navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [PRINTER_SERVICE_UUID, PRINTER_SERVICE_UUID_ALT],
    });
  });

  await _connect(device);
  _device = device;

  // Reconectar automáticamente si se desconecta
  device.addEventListener('gattserverdisconnected', () => {
    _characteristic = null;
    setTimeout(() => reconnect(), 2000);
  });
}

async function _connect(device: BluetoothDevice): Promise<void> {
  const server = await device.gatt!.connect();

  // Intentar con UUID estándar primero, luego alternativo
  let service: BluetoothRemoteGATTService;
  let characteristic: BluetoothRemoteGATTCharacteristic;

  try {
    service        = await server.getPrimaryService(PRINTER_SERVICE_UUID);
    characteristic = await service.getCharacteristic(PRINTER_CHARACTERISTIC_UUID);
  } catch {
    service        = await server.getPrimaryService(PRINTER_SERVICE_UUID_ALT);
    characteristic = await service.getCharacteristic(PRINTER_CHARACTERISTIC_UUID_ALT);
  }

  _device         = device;
  _characteristic = characteristic;
}

export function disconnect(): void {
  _device?.gatt?.disconnect();
  _device         = null;
  _characteristic = null;
}

// ─── Enviar datos a la impresora ──────────────────────────────────────────────

/** Envía datos en chunks de 512 bytes (límite BLE MTU) */
async function send(data: Uint8Array): Promise<void> {
  if (!_characteristic) throw new Error('Impresora no conectada');

  const CHUNK = 512;
  for (let i = 0; i < data.length; i += CHUNK) {
    await _characteristic.writeValueWithoutResponse(data.slice(i, i + CHUNK));
    await new Promise(r => setTimeout(r, 20)); // pequeña pausa entre chunks
  }
}

// ─── Formatear ticket ESC/POS ─────────────────────────────────────────────────

export interface TicketData {
  storeName:    string;
  storePhone?:  string;
  storeAddress?: string;
  items:        { key: string; value: string }[];
  footer?:      string;
}

export function buildTicket(ticket: TicketData): Uint8Array {
  const parts: Uint8Array[] = [];

  const ln  = (text: string) => concat(encode(text), bytes(...CMD.LINE_FEED));
  const cmd  = (...c: number[]) => bytes(...c);

  // ── Inicializar ──
  parts.push(cmd(...CMD.INIT));

  // ── Encabezado ──
  parts.push(cmd(...CMD.ALIGN_CENTER));
  parts.push(cmd(...CMD.BOLD_ON));
  parts.push(cmd(...CMD.DOUBLE_SIZE));
  parts.push(ln(ticket.storeName));
  parts.push(cmd(...CMD.NORMAL_SIZE));
  parts.push(cmd(...CMD.BOLD_OFF));

  if (ticket.storePhone)   parts.push(ln(`Tel: ${ticket.storePhone}`));
  if (ticket.storeAddress) parts.push(ln(ticket.storeAddress));

  parts.push(cmd(...CMD.ALIGN_LEFT));
  parts.push(ln(divider('=')));

  // ── Datos de la transacción ──
  for (const item of ticket.items) {
    if (!item.key && !item.value) {
      parts.push(ln(divider('-')));
      continue;
    }
    // Claves especiales en negrita (Monto, Total)
    const isBold = /monto|total|amount|precio/i.test(item.key);
    if (isBold) parts.push(cmd(...CMD.BOLD_ON));
    parts.push(ln(row(item.key, item.value)));
    if (isBold) parts.push(cmd(...CMD.BOLD_OFF));
  }

  parts.push(ln(divider('=')));

  // ── Footer ──
  parts.push(cmd(...CMD.ALIGN_CENTER));
  parts.push(ln(ticket.footer || '¡Gracias por su preferencia!'));
  parts.push(ln('Powered by Pay+'));
  parts.push(ln(''));

  // ── Corte ──
  parts.push(cmd(...CMD.FEED_3));
  parts.push(cmd(...CMD.CUT));

  return concat(...parts);
}

// ─── API pública principal ────────────────────────────────────────────────────

/**
 * Imprime un ticket usando los datos del modal de detalle de transacción.
 * @param items  Array de {key, value} que devuelve /get-transaction-details
 * @param storeName  Nombre del negocio (del perfil del usuario)
 */
export async function printTicket(
  items: { key: string; value: string }[],
  storeName: string = 'Pay+ POS',
  storePhone?: string,
): Promise<void> {
  if (!_characteristic) {
    throw new Error('Impresora no conectada. Ve a Configuración → Impresora para conectar.');
  }

  // Filtrar items vacíos y reconstruir con separadores legibles
  const cleanItems = items.filter(i => i.key && i.value);

  const ticketData: TicketData = {
    storeName,
    storePhone,
    items: cleanItems,
    footer: '¡Gracias por su compra!',
  };

  const data = buildTicket(ticketData);
  await send(data);
}

/** Imprime un ticket de prueba para verificar la conexión */
export async function printTestTicket(): Promise<void> {
  const items = [
    { key: 'Fecha',     value: new Date().toLocaleString('es-DO') },
    { key: 'Operación', value: 'PRUEBA DE IMPRESIÓN' },
    { key: 'Estado',    value: 'OK' },
    { key: 'Monto',     value: 'RD$0.00' },
  ];
  await printTicket(items, 'Pay+ POS — Test');
}
